/* eslint-disable */
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  inject,
  ViewChild,
  ElementRef,
  Type,
  input,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { Clipboard } from '@angular/cdk/clipboard';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { customSortWithEmptyValuesOnTop } from '../../helpers/helpers';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';

import { MatCheckbox } from '@angular/material/checkbox';
import { FilterMenuComponent } from './filter-menu/filter-menu.component';
import { TableComponentWrapperComponent } from './table-component-wrapper';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { ColumnFilterService } from '../../services/column-filter.service';
import { NotificationService } from '../../services/notification.service';
import { INotification } from '../../interfaces/INotification';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { PaginationRangePipe } from '../../pipes/pagination-range.pipe';
import { IFilter } from '../../interfaces/IFilter';
import { SelectedFilterOptionsPipe } from './pipes/selected-filter-options.pipe';
import { TABLE_MODE } from '../../enums/TableMode.enum';
import { SortIcon } from '../../types/SortIcon.types';
import { LoadingComponent } from '../loading/loading.component';
import { LoadingMode } from '../../enums/LoadingMode.enum';
import { TravelTodosOperations } from '../../../travels/operations/travel-todos.operations';
import { ProjectOperations } from 'src/app/projects/operations/project.operations';
import { EmployeeOperations } from '../../operations/employee.operations';
import { EmployeePickerOperations } from '../people-picker/employee-picker-dialog/operations/employee-picker.operations';

export interface TableColumn<T> {
  /**
   * The name to use for the column.
   */
  name: string;

  /**
   * The name parameter if the string has a parameter that doesn't need translating.
   */
  nameParam?: string;

  /**
   * Whether the column is a unique identifier.
   * Needed for the selection.
   * @default false
   */
  id?: boolean;

  /**
   * The selector to use for the column.
   */
  selector?: string;

  /**
   * The function to use for the column.
   * @param row The row to get the values from.
   * @returns The value to display.
   */
  row?: (row: T) => string | number | boolean | null | undefined;

  /**
   * The value to display in the column footer.
   * Applied only if the table option hasFooter is enabled.
   * @returns The value to display.
   */
  footerValue?: number | string;

  /**
   * The function to use for the column footer.
   * @param rows The rows to get the values from.
   * @returns The value to display.
   */
  footerFn?: (rows: T[]) => string;

  /**
   * Renders a component for the cell.
   * @param row The row to get the values from.
   */
  component?: Type<any>;

  /**
   * Callback to initialize the component.
   * @param row The row to get the values from.
   */
  componentInit?: (component: any, row: T) => void;

  /**
   * Whether the column is sortable.
   * @default false
   * @choices true | false
   * @type boolean
   */
  sortable?: boolean;

  /**
   * Whether the column is filterable.
   * @default false
   * @choices true | false
   * @type boolean
   */
  filterable?: boolean;

  /**
   * Whether the filter options should be displayed as checkboxes or as a search field.
   * @default 'checkbox'
   * @choices 'checkbox' | 'search'
   * @type string
   */
  filterType?: 'checkbox' | 'search';

  /**
   * Filter options for this column.
   * Defaults to the values of the column for mode 'client'.
   */
  filterOptions?: TableFilterOption[];

  /**
   * Custom display function for filter labels.
   * @param value The value to display.
   * @returns The display value.
   */
  filterDisplayFn?: (value: any) => string;

  /**
   * Custom filter function.
   * @param values The value to filter.
   * @param row The row to get the values from.
   * @returns {boolean} Whether the row should be displayed.
   */
  filterFn?: (values: TableFilterOptionValue[], row: T) => boolean;

  /**
   * Custom sort function.
   * @param a The first row to compare.
   * @param b The second row to compare.
   * @returns {number} The sort order. -1 for a < b, 0 for a == b, 1 for a > b.
   */
  sortFn?: (a: T, b: T) => number;

  /**
   * Specifies the minimum width of the column.
   * @default 'auto'
   * @type string
   * @example '100px'
   */
  minWidth?: string;

  /**
   * The group name if the column is the beginning of a column group from the upper header.
   */
  groupName?: string;

  /**
   * The group name parameter if the string has a parameter that doesn't need translating.
   */
  groupNameParam?: string;

  /**
   * The number of columns in the group starting from this column.
   */
  groupColSpan?: number;

  /**
   * Indicates whether persistent filter is applied to the column
   * Used to change the column header backgroundColor
   */
  persistentFilterApplied?: boolean;

  /**
   * Indicates whether the column header should be wrapped
   */
  wrapColumnHeaderText?: boolean;

  /**
   * Returns the raw value for copying to clipboard.
   * When defined, a copy icon is rendered next to the cell value.
   * @param row The row to get the value from.
   * @returns The raw value to copy.
   */
  copyValue?: (row: T) => string | number;
}

export interface TableFilterOption {
  /**
   * The value to use for the filter.
   */
  value: TableFilterOptionValue;

  /**
   * The label to use for the filter.
   */
  label: string;
}

export type TableFilterOptionValue = string | number;

interface TableFilter {
  sort: 'asc' | 'desc' | undefined;
  options: TableFilterOptionValue[];
}

export type TableFilterState = { [key: string]: TableFilter };

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    InfiniteScrollDirective,
    MatCheckbox,
    FilterMenuComponent,
    CdkDropList,
    CdkDrag,
    TableComponentWrapperComponent,
    MatProgressSpinner,
    MatSelect,
    MatOption,
    TranslatePipe,
    PaginationRangePipe,
    SelectedFilterOptionsPipe,
    LoadingComponent,
    ScrollingModule,
  ],
})
export class TableComponent implements OnChanges, AfterViewInit, OnInit {
  private cdr = inject(ChangeDetectorRef);
  private translateService = inject(TranslateService);
  private filterSortingService = inject(ColumnFilterService);
  private clipboardService = inject(Clipboard);
  private notificationService = inject(NotificationService);

  /**
   * The configuration for the columns.
   */
  readonly columns = input<TableColumn<any>[]>();

  /**
   * The data to display.
   */
  readonly data = input<any[]>();

  /**
   * The applied server filters
   */
  readonly serverFilters = input<IFilter[]>([]);

  /**
   * The data to display.
   */
  readonly serverSort = input<string>();

  /**
   * Whether rows can be selected.
   */
  readonly selectable = input(false);

  /**
   * Whether to show the pagination.
   * In client mode, the pagination is also performed automatically.
   * @default false
   */
  readonly pagination = input(false);

  /**
   * The page size to use for the pagination.
   * @default 10
   */
  @Input() pageSize = 50;

  /**
   * The page size options to use for the pagination.
   * @default [10, 25, 50, 100]
   */
  readonly pageSizeOptions = input<number[]>([15, 30, 50]);

  /**
   * The current page.
   * @default 1
   */
  @Input() page?: number = 1;

  /**
   * The total number of items. Required for server mode pagination.
   * Client mode pagination will automatically calculate the total number of items.
   */
  readonly total = input<number>();

  /**
   * The mode to use for the table.
   * Client mode will automatically filter, sort and paginate the data.
   * Server mode requires more input in order to work correctly
   * This includes totalCount, .pagination, pageSize, page, serverSort, serverFilters, endpoint
   * @default 'client'
   */
  readonly mode = input<TABLE_MODE>(TABLE_MODE.CLIENT);

  /**
   * The endpoint to use for the table.
   * Required for server mode pagination.
   * Used for fetching the column filter options
   */
  readonly endpoint = input<string>();

  /**
   * Whether the table is loading.
   * @default false
   */
  readonly loading = input(false);

  /**
   * Select all rows.
   * @default false
   * @choices true | false
   * @type boolean
   */
  readonly selectAllRows = input(false);

  /**
   * Selected rows.
   * @default []
   */
  @Input() selectedRows: any[] = [];

  /**
   * If multiple select of items should be allowed.
   * @default true
   */
  readonly multipleSelect = input<boolean>(true);

  /**
   * Whether to automatically apply the filter.
   * @default false
   * @choices true | false
   * @type boolean
   */
  readonly automaticFilterApply = input(false);

  /**
   * Indicates whether the table should be prefiltered/presorted if provided
   * The key should be the column selector
   * The value should be of @type TableFilter
   * The value sort defines the presorting and the value options array the preselected filters
   * @type TableFilterState
   */
  readonly preSelectedFilterOptions = input<TableFilterState>();

  /**
   * Custom style for the table.
   * @default ''
   * @type string
   */
  readonly style = input<string>();

  /**
   * Whether a footer row should be added.
   * @default false
   * @type boolean
   */
  readonly hasFooter = input(false);

  /**
   * Whether an upper header row that groups columns should be added.
   * @default false
   * @type boolean
   */
  readonly hasColumnGroups = input(false);

  /**
   * Defined wheter clickin on a row should emit the rowClick event.
   */
  readonly emitRowClick = input(false);

  /**
   * Defined wheter drag and drop should be allowed for rows.
   */
  readonly isRowDragAndDropAllowed = input(false); /**

   * Used to apply lower height for the table so that we do not have dialog scroll bar.
   */
  readonly isTableInsideDialog = input(false);

  protected readonly itemsPerLoad = input<number>(20);

  readonly currentView = input<string>();

  readonly employeeFilter = input<IFilter[]>(null);

  /**
   * Handles the filter change event, only emit the changed filter.
   */
  readonly filterChange = output<TableFilterState>();

  /**
   * Handles the filter change event in server mode, emits all applied filters, including filters with values as an empty array.
   */
  readonly serverFilterChange = output<IFilter[]>();

  /**
   * Handles the sort change event, only emit the changed sort.
   */
  readonly sortChange = output<TableFilterState>();

  /**
   * Handles the sort change event in server mode, emits the sort in the following format: 'column,sortDirection' or '' if no sort is applied.
   */
  readonly serverSortChange = output<string>();

  /**
   * Handles the filter state change event, emit the complete filter state.
   * This is useful for server mode pagination.
   */
  readonly filterState = output<TableFilterState>();

  /**
   * Handles the page change event. Emits current page, page size and filter state.
   * This is useful for server mode pagination.
   */
  readonly pageChange = output<{
    page: number;
    size: number;
    filters: TableFilterState;
  }>();

  /**
   * Handles the selection change event.
   * Emits the selected rows.
   */
  readonly selectionChange = output<any[]>();

  /**
   * Handles the select all event.
   * Returns true if select all is checked, false otherwise.
   */
  readonly selectAllChange = output<boolean>();

  /**
   * Handles the row click event.
   * Emits the clicked row.
   */
  readonly rowClick = output<any>();

  /**
   * Emits the new order of the elements after drag and drop.
   */
  readonly elementsOrderChanged = output<any[]>();

  readonly filteredRowsChanged = output<any[]>();

  protected filters: { [key: string]: TableFilter } = {};
  protected selectAll = false;
  protected selectionKey: string;
  protected displayedRows: any[] = [];
  protected currentPage = 1;
  protected pageSizeOptionsToDisplay = computed(() => {
    return [...this.pageSizeOptions(), this.totalSize].sort((value1, value2) => value1 - value2);
  });
  private dataSize = 0;
  private filteredAndSortedRows: any[] = [];
  protected indeterminateTableHead: boolean = false;
  protected readonly LoadingMode = LoadingMode;

  protected resetAndLoadItems(): void {
    if (!this.pagination()) {
      this.currentPage = 1;
      this.displayedRows = [];
      this.filteredAndSortedRows = this.rows; // Trigger filtering and sorting
      this.loadMoreItems();
    }
  }

  protected get rows() {
    if (this.mode() === TABLE_MODE.SERVER) {
      return this.data();
    }

    return this.getClientSideRows();
  }

  protected loadMoreItems(): void {
    if (this.displayedRows.length === this.filteredAndSortedRows.length) {
      return;
    }
    const start = (this.currentPage - 1) * this.itemsPerLoad();
    const end = start + this.itemsPerLoad();
    this.displayedRows = [...structuredClone(this.displayedRows), ...structuredClone(this.filteredAndSortedRows).slice(start, end)];
    this.currentPage++;
  }

  protected get totalSize() {
    return this.total() || this.dataSize;
  }

  ngOnInit() {
    // Initialize filters
    if (!this.preSelectedFilterOptions()) {
      this.initializeFiltersWithNoPersistentFilters();
    }

    // Initialize selection
    const keyColumn = this.columns().find(column => column.id);

    const selectable = this.selectable();
    if (selectable && keyColumn === undefined) {
      console.error("[SAG Table]: Property 'selectable' requires a column with 'id' set.");
    } else if (selectable) {
      this.selectionKey = keyColumn.selector;
    }

    this.resetAndLoadItems();
  }

  private initializeFiltersWithNoPersistentFilters(): void {
    if (this.mode() === TABLE_MODE.CLIENT) {
      this.columns().forEach(column => {
        this.filters[column.selector] = {
          sort: undefined,
          options: [],
        };

        column.filterType = column.filterType || 'checkbox';
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.preSelectedFilterOptions) {
      this.initializeFilters();
    } else if (changes.columns) {
      if (!this.preSelectedFilterOptions()) {
        this.initializeFiltersWithNoPersistentFilters();
      } else {
        this.initializeFilters();
      }
    } else if (changes.data) {
      this.resetAndLoadItems();
    }
  }

  ngAfterViewInit(): void {
    this.resetAndLoadItems();
  }

  drop(event: CdkDragDrop<any[]>) {
    if (this.isRowDragAndDropAllowed()) {
      moveItemInArray(this.displayedRows, event.previousIndex, event.currentIndex);
      // If you're using server-side data or need to emit an event after reordering, do it here
      this.elementsOrderChanged.emit(this.displayedRows);
      this.cdr.markForCheck(); // Trigger change detection if needed
    }
  }

  // ############ SORTING ############
  protected toggleSort(column: string): void {
    if (this.mode() === TABLE_MODE.SERVER) {
      this.toggleServerSideSort(column);
    } else {
      this.toggleClientSideSort(column);
    }
  }

  private toggleClientSideSort(column: string): void {
    if (this.filters[column].sort === undefined) {
      this.sort(column, 'asc');
    } else if (this.filters[column].sort === 'asc') {
      this.sort(column, 'desc');
    } else {
      this.sort(column, undefined);
    }
  }

  private toggleServerSideSort(column: string): void {
    const serverSort = this.serverSort();
    if (serverSort) {
      const [serverSortedColumn, serverSortDirection] = serverSort.split(',');
      if (serverSortedColumn === column) {
        if (serverSortDirection === 'ASC') {
          this.serverSortChange.emit(`${column},DESC`);
        } else {
          this.serverSortChange.emit(''); // Reset the sort
        }
      } else {
        this.serverSortChange.emit(`${column},ASC`);
      }
    } else {
      this.serverSortChange.emit(`${column},ASC`);
    }
  }
  protected getSortIcon(column: string): SortIcon {
    if (this.mode() === TABLE_MODE.SERVER) {
      return this.getServerSideSortIcon(column);
    } else {
      return this.getClientSideSortIcon(column);
    }
  }

  private getClientSideSortIcon(column: string): SortIcon {
    if (this.filters[column].sort === undefined) {
      return 'Sort-up-down';
    } else if (this.filters[column].sort === 'asc') {
      return 'Sort-up';
    } else {
      return 'Sort-down';
    }
  }

  private getServerSideSortIcon(column: string): SortIcon {
    const serverSort = this.serverSort();
    if (serverSort) {
      const [serverSortedColumn, serverSortDirection] = serverSort.split(',');
      if (serverSortedColumn === column) {
        return serverSortDirection === 'ASC' ? 'Sort-up' : 'Sort-down';
      }
    }

    return 'Sort-up-down';
  }

  protected sortActive(column: string): boolean {
    if (this.mode() === TABLE_MODE.SERVER) {
      return this.getServerSideSortActive(column);
    } else {
      return this.filters[column].sort !== undefined;
    }
  }

  private getServerSideSortActive(column: string): boolean {
    const serverSort = this.serverSort();
    const serverSortedColumn = serverSort ? serverSort.split(',')[0] : null;
    return serverSortedColumn === column;
  }

  protected sort(columns: string, value: 'asc' | 'desc') {
    if (this.filters[columns] === undefined) {
      this.filters[columns] = {
        sort: undefined,
        options: [],
      };
    }

    Object.keys(this.filters).forEach(key => {
      if (key !== columns) {
        this.filters[key].sort = undefined;
      }
    });

    this.filters[columns].sort = value;

    this.resetAndLoadItems();
    this.sortChange.emit(this.filters);
    this.cdr.markForCheck();
  }

  protected getFilterOptions(column: string): TableFilterOption[] | Observable<TableFilterOption[]> {
    if (this.mode() === TABLE_MODE.CLIENT) {
      return this.getClientSideFilterOptions(column);
    } else {
      return this.getServerSideFilterOptions(column);
    }
  }

  private getServerSideFilterOptions(column: string): Observable<TableFilterOption[]> {
    const currentView = this.currentView();
    if (currentView && column === 'status') {
      return of(TravelTodosOperations.getStatusesFilterOptions(currentView, this.translateService));
    }
    if (column === 'pue' || column === 'pk' || column === 'sp') {
      return of(ProjectOperations.getFrontendCalculatedFilterOptions(column));
    }
    if (column === 'hasOngoingWorkflow') {
      return of(EmployeeOperations.getFrontendCalculatedFilterOptions(column));
    }
    const columnObject = this.columns().find(col => col.selector === column);
    if (this.employeeFilter() && this.employeeFilter().find(f => f.key === column)) {
      return of(EmployeePickerOperations.getLimitedFilterOptions(this.employeeFilter().find(f => f.key === column)));
    }
    return this.filterSortingService.fetchFilterOptions(this.endpoint(), column).pipe(
      map(options =>
        options
          .filter(option => option !== null)
          .map(option => ({
            value: option?.toString(),
            label: columnObject?.filterDisplayFn ? columnObject.filterDisplayFn(option) : option.toString(),
          })),
      ),
    );
  }

  private getClientSideFilterOptions(column: string): TableFilterOption[] {
    const columnObj = this.columns().find(col => col.selector === column);
    if (columnObj?.filterOptions) {
      return columnObj?.filterOptions.sort((option1, option2) =>
        customSortWithEmptyValuesOnTop(
          typeof option1.value === 'number' ? option1.value : option1.label,
          typeof option2.value === 'number' ? option2.value : option2.label,
          this.translateService,
        ),
      );
    }

    const options = new Set<string>();
    this.data().forEach(row => {
      options.add(row[column]);
    });

    return Array.from(options)
      .map(option => {
        return {
          value: option,
          label: columnObj?.filterDisplayFn ? columnObj.filterDisplayFn(option) : option,
        };
      })
      .filter(option => option.value)
      .sort((option1, option2) =>
        customSortWithEmptyValuesOnTop(
          typeof option1.value === 'number' ? option1.value : option1.label,
          typeof option2.value === 'number' ? option2.value : option2.label,
          this.translateService,
        ),
      );
  }

  protected filterArray(columns: string, values: TableFilterOptionValue[]): void {
    if (this.mode() === TABLE_MODE.SERVER) {
      this.applyServerSideFilters(columns, values);
    } else {
      this.applyClientSideFilter(columns, values);
    }
  }

  private applyServerSideFilters(columns: string, values: TableFilterOptionValue[]): void {
    const filter = this.serverFilters().find(filter => filter.key === columns);
    if (filter) {
      filter.values = values;
    } else {
      this.serverFilters().push({ key: columns, values });
    }
    this.serverFilterChange.emit(this.serverFilters());
  }

  private applyClientSideFilter(columns: string, values: TableFilterOptionValue[]): void {
    const filters = this.filters;
    if (filters[columns] === undefined) {
      filters[columns] = {
        sort: undefined,
        options: values,
      };
    } else {
      filters[columns].options = values;
    }

    this.resetAndLoadItems();
    this.filterChange.emit(this.filters);
    this.cdr.markForCheck();
    this.filteredRowsChanged.emit(this.displayedRows);
  }

  // ############ FOOTER ############
  protected getFooterValue(column: TableColumn<any>) {
    if (column.footerFn) {
      return column.footerFn(this.rows);
    }

    return column.footerValue || '';
  }

  // ############ PAGINATION ############
  protected previousPage() {
    if (this.page === 1 || this.loading()) {
      return;
    }

    this.page = this.page - 1;
    this.pageChange.emit({
      page: this.page,
      size: this.pageSize,
      filters: this.filters,
    });
  }

  protected nextPage() {
    if (this.page * this.pageSize >= this.totalSize || this.loading()) {
      return;
    }

    this.page = this.page + 1;
    this.pageChange.emit({
      page: this.page,
      size: this.pageSize,
      filters: this.filters,
    });
  }

  protected changePageSize(pageSize: number) {
    this.pageSize = pageSize;
    this.page = 1;
    this.pageChange.emit({
      page: this.page,
      size: this.pageSize,
      filters: this.filters,
    });
  }

  // ############ SELECTION ############
  protected toggleSelectAll(checked: boolean) {
    this.selectAll = checked;

    if (this.selectAll) {
      this.selectedRows = this.rows;
    } else {
      this.selectedRows = [];
    }

    this.selectionChange.emit(this.selectAll ? this.rows : []);
    this.selectAllChange.emit(this.selectAll);
  }

  protected toggleSelect(row: any) {
    const index = this.selectedRows.findIndex((row_: any) => row_[this.selectionKey] === row[this.selectionKey]);
    if (index === -1) {
      if (!this.multipleSelect()) {
        // Removing all the items from the array without creating new reference to it
        this.selectedRows.splice(0, this.selectedRows.length);
      }
      this.selectedRows.push(this.rows.find((row_: any) => row_[this.selectionKey] === row[this.selectionKey]));
    } else {
      if (this.selectAll) this.indeterminateTableHead = true;
      this.selectedRows.splice(index, 1);
    }

    if (this.selectedRows.length === this.rows.length) {
      this.indeterminateTableHead = true;
    } else {
      this.selectAll = false;
      this.indeterminateTableHead = false;
    }

    this.selectionChange.emit(this.selectedRows);
  }

  protected isSelected(row: any, selectAll: boolean) {
    return selectAll || this.selectedRows.find((row_: any) => row_[this.selectionKey] === row[this.selectionKey]) !== undefined;
  }

  protected copyToClipboard(value: string | number, event: Event): void {
    event.stopPropagation();
    this.clipboardService.copy(String(value));
    const notification: INotification = {
      type: 'Success',
      title: 'default.success',
      desc: 'account.accountValueCopied',
    };
    this.notificationService.showMessage(notification);
  }

  protected rowClicked(row: any) {
    if (this.emitRowClick()) {
      this.rowClick.emit(row);
    }
  }

  private initializeFilters(): void {
    this.columns().forEach(column => {
      const preSelectedFilterOptions = this.preSelectedFilterOptions();
      const preSelectedFilterOptionsValue = this.preSelectedFilterOptions();
      this.filters[column.selector] = {
        sort: preSelectedFilterOptions
          ? preSelectedFilterOptions[column.selector]
            ? preSelectedFilterOptions[column.selector].sort
            : undefined
          : undefined,
        options: preSelectedFilterOptionsValue
          ? preSelectedFilterOptionsValue[column.selector]
            ? preSelectedFilterOptionsValue[column.selector].options
            : []
          : [],
      };

      // Initialize prefiltered option styles
      const preSelectedFilterOptionsVal = this.preSelectedFilterOptions();
      if (preSelectedFilterOptionsVal) {
        const areAllOptionsSelected =
          this.mode() !== TABLE_MODE.SERVER &&
          this.data().every(row => this.filters[column.selector].options.includes(row[column.selector]));
        const areOptionsAvailable = this.filters[column.selector].options.length !== 0;
        column.persistentFilterApplied = !!preSelectedFilterOptionsVal[column.selector] && !areAllOptionsSelected && areOptionsAvailable;
      } else {
        column.persistentFilterApplied = false;
      }

      column.filterType = column.filterType || 'checkbox';
    });
  }

  private getClientSideRows() {
    let rows = this.data();
    Object.keys(this.filters).forEach(key => {
      const filter = this.filters[key];
      if (filter.options.length > 0) {
        rows = rows.filter(row => {
          const column = this.columns().find(col => col.selector === key);
          if (column?.filterFn) {
            return column.filterFn(filter.options, row);
          } else {
            return filter.options.includes(row[key]);
          }
        });
      }
    });

    // Sort
    Object.keys(this.filters).forEach(key => {
      const filter = this.filters[key];
      if (filter.sort !== undefined) {
        rows = rows.sort((a, b) => {
          const column = this.columns().find(col => col.selector === key);
          if (column?.sortFn) {
            return column.sortFn(a, b) * (filter.sort === 'asc' ? 1 : -1);
          } else {
            if (a[key] < b[key]) {
              return filter.sort === 'asc' ? -1 : 1;
            }
            if (a[key] > b[key]) {
              return filter.sort === 'asc' ? 1 : -1;
            }
            return 0;
          }
        });
      }
    });

    this.dataSize = rows.length;

    // Pagination
    if (this.pagination()) {
      const start = (this.page - 1) * this.pageSize;
      const end = start + this.pageSize;
      rows = rows.slice(start, end);
    }

    return rows;
  }
}
